import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './UnderwriterPortal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_CARDS = [
  { key: 'all', label: 'Total Intake', detail: 'All applications in the queue' },
  { key: 'under_review', label: 'Under Review', detail: 'Ready for underwriting analysis' },
  { key: 'approved', label: 'Approved', detail: 'Completed with acceptance' },
  { key: 'rejected', label: 'Rejected', detail: 'Closed with decline decision' },
  { key: 'pending_info', label: 'Pending Info', detail: 'Waiting on applicant follow-up' },
  { key: 'escalated', label: 'Escalated', detail: 'Needs senior clinical attention' },
];

function UnderwriterPortal({ onSwitchToChat }) {
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [decisionModal, setDecisionModal] = useState(null); // { applicantId, action: 'approved'|'rejected' }
  const [activeTab, setActiveTab] = useState('applications');
  const [claims, setClaims] = useState([]);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimFilter, setClaimFilter] = useState('all');
  const [preauths, setPreauths] = useState([]);
  const [selectedPreauth, setSelectedPreauth] = useState(null);
  const [preauthFilter, setPreauthFilter] = useState('all');

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/applications`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setApplications(data.applications || []);
    } catch (err) {
      setError('Failed to load applications. Please try again.');
      console.error('Failed to fetch applications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const fetchClaims = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/claims/all`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setClaims(data.claims || []);
    } catch (err) {
      console.error('Failed to fetch claims:', err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'claims') fetchClaims();
  }, [activeTab, fetchClaims]);

  const fetchPreauths = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/preauth/all`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setPreauths(data.preauths || []);
    } catch (err) {
      console.error('Failed to fetch preauths:', err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'preauth') fetchPreauths();
  }, [activeTab, fetchPreauths]);

  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleUpdateStatus = async (applicantId, newStatus, decisionPayload = null) => {
    // For approve/reject without a payload, open the decision modal
    if ((newStatus === 'approved' || newStatus === 'rejected') && !decisionPayload) {
      setDecisionModal({ applicantId, action: newStatus });
      return;
    }

    try {
      const body = { applicant_id: applicantId, status: newStatus, ...decisionPayload };
      const resp = await fetch(`${API_URL}/application-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      setApplications(prev =>
        prev.map(app => (app.applicant_id === applicantId ? { ...app, status: newStatus } : app)),
      );

      if (selectedApp && selectedApp.applicant_id === applicantId) {
        setSelectedApp(prev => ({ ...prev, status: newStatus }));
      }

      const statusLabels = { approved: 'Approved', rejected: 'Rejected', pending_info: 'Pending Info', escalated: 'Escalated' };
      const extra = data.policy_number ? ` Policy: ${data.policy_number}` : '';
      showToast(`${applicantId} → ${statusLabels[newStatus] || newStatus}. Applicant has been notified.${extra}`, newStatus === 'rejected' ? 'error' : 'success');
    } catch (err) {
      console.error('Failed to update status:', err);
      showToast('Failed to update status. Please try again.', 'error');
    }
  };

  const handleDecisionConfirm = (applicantId, newStatus, payload) => {
    setDecisionModal(null);
    handleUpdateStatus(applicantId, newStatus, payload);
  };

  const handleClaimDecision = async (claimId, decision, approvedAmount, reason) => {
    try {
      const resp = await fetch(`${API_URL}/claim-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claim_id: claimId, decision, approved_amount: approvedAmount, reason }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      showToast(`Claim ${claimId} ${decision}`, decision === 'approved' ? 'success' : 'error');
      fetchClaims();
      setSelectedClaim(null);
    } catch (err) {
      showToast('Failed to process claim decision', 'error');
    }
  };

  const filteredApps = applications.filter((app) => {
    const matchesFilter = filter === 'all' || app.status === filter;
    const needle = searchTerm.toLowerCase();
    const matchesSearch = !needle
      || (app.applicant_name || '').toLowerCase().includes(needle)
      || (app.applicant_id || '').toLowerCase().includes(needle)
      || (app.email || '').toLowerCase().includes(needle);

    return matchesFilter && matchesSearch;
  });

  const statusCounts = {
    all: applications.length,
    under_review: applications.filter(app => app.status === 'under_review').length,
    approved: applications.filter(app => app.status === 'approved').length,
    rejected: applications.filter(app => app.status === 'rejected').length,
    pending_info: applications.filter(app => app.status === 'pending_info').length,
    escalated: applications.filter(app => app.status === 'escalated').length,
  };

  const urgentCount = statusCounts.under_review + statusCounts.pending_info + statusCounts.escalated;
  const aiReadyCount = applications.filter(app => app.pdf_parsed || app.health_status).length;

  if (selectedApp) {
    return (
      <>
        {toast && (
          <div className={`uw-toast uw-toast-${toast.type}`}>
            <span className="uw-toast-icon">
              {toast.type === 'success' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
              )}
            </span>
            <span className="uw-toast-msg">{toast.message}</span>
          </div>
        )}
        <ApplicationDetail
          app={selectedApp}
          onBack={() => setSelectedApp(null)}
          onUpdateStatus={handleUpdateStatus}
          onAskAI={(id) => onSwitchToChat && onSwitchToChat(id)}
        />
        {decisionModal && (
          <DecisionModal
            applicantId={decisionModal.applicantId}
            action={decisionModal.action}
            onConfirm={handleDecisionConfirm}
            onCancel={() => setDecisionModal(null)}
          />
        )}
      </>
    );
  }

  return (
    <div className="uw-shell">
      {toast && (
        <div className={`uw-toast uw-toast-${toast.type}`}>
          <span className="uw-toast-icon">
            {toast.type === 'success' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
            )}
          </span>
          <span className="uw-toast-msg">{toast.message}</span>
        </div>
      )}
      <section className="uw-hero">
        <div className="uw-hero-copy">
          <span className="eyebrow">Underwriter command center</span>
          <h1 className="uw-title">Operational visibility for fast, defensible underwriting decisions.</h1>
          <p className="uw-subtitle">
            Review new intake, spot escalations, and move between AI guidance and manual decisions without leaving the workspace.
          </p>

          <div className="uw-hero-metrics">
            <div className="uw-hero-metric">
              <span className="uw-hero-metric-value">{urgentCount}</span>
              <span className="uw-hero-metric-label">Open actions</span>
            </div>
            <div className="uw-hero-metric">
              <span className="uw-hero-metric-value">{aiReadyCount}</span>
              <span className="uw-hero-metric-label">AI-enriched files</span>
            </div>
            <div className="uw-hero-metric">
              <span className="uw-hero-metric-value">{statusCounts.approved + statusCounts.rejected}</span>
              <span className="uw-hero-metric-label">Resolved decisions</span>
            </div>
          </div>
        </div>

        <div className="uw-hero-media">
          <div className="uw-hero-chip">Live queue overview</div>
          <div className="uw-hero-card">
            <span className="uw-hero-card-label">Priority queue</span>
            <strong>{statusCounts.escalated} escalated</strong>
            <p>{statusCounts.pending_info} files are waiting for supporting information.</p>
          </div>
        </div>
      </section>

      {error && <div className="uw-error">{error}</div>}

      <div className="uw-tabs" style={{ display: 'flex', gap: '8px', padding: '0 24px', marginBottom: '16px' }}>
        <button className={`seg-btn ${activeTab === 'applications' ? 'seg-btn-active' : ''}`} onClick={() => setActiveTab('applications')}>Applications</button>
        <button className={`seg-btn ${activeTab === 'claims' ? 'seg-btn-active' : ''}`} onClick={() => setActiveTab('claims')}>Claims</button>
        <button className={`seg-btn ${activeTab === 'preauth' ? 'seg-btn-active' : ''}`} onClick={() => setActiveTab('preauth')}>Pre-Authorizations</button>
      </div>

      {activeTab === 'applications' && (<>
      <section className="uw-toolbar surface-card">
        <div className="uw-toolbar-copy">
          <div className="uw-toolbar-title">Portfolio filters</div>
          <p>Slice the queue by status or search directly by applicant, email, or application ID.</p>
        </div>

        <div className="uw-toolbar-actions">
          <label className="uw-search-wrap">
            <span>Search queue</span>
            <input
              type="text"
              className="uw-search"
              placeholder="Search by name, ID, or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>

          <button className="uw-refresh-btn" onClick={fetchApplications} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh queue'}
          </button>
        </div>
      </section>

      <div className="uw-stats">
        {STATUS_CARDS.map((stat) => (
          <button
            key={stat.key}
            className={`uw-stat-card ${filter === stat.key ? 'active' : ''}`}
            onClick={() => setFilter(stat.key)}
          >
            <span className="uw-stat-label">{stat.label}</span>
            <span className="uw-stat-count">{statusCounts[stat.key]}</span>
            <span className="uw-stat-detail">{stat.detail}</span>
          </button>
        ))}
      </div>

      <section className="uw-table-wrap surface-card">
        <div className="uw-table-head">
          <div>
            <div className="uw-table-title">Application review queue</div>
            <p>{filteredApps.length} application{filteredApps.length === 1 ? '' : 's'} match the current filters.</p>
          </div>
          <span className="uw-table-pill">{filter === 'all' ? 'All statuses' : formatStatus(filter)}</span>
        </div>

        {loading ? (
          <div className="uw-loading">Loading applications…</div>
        ) : filteredApps.length === 0 ? (
          <div className="uw-empty">No applications match your current filter.</div>
        ) : (
          <div className="uw-table-scroll">
            <table className="uw-table">
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Applicant</th>
                  <th>Risk profile</th>
                  <th>Coverage</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map(app => (
                  <tr key={app.applicant_id}>
                    <td>
                      <div className="uw-cell-id">{app.applicant_id}</div>
                      <div className="uw-cell-sub">{app.email || 'No email provided'}</div>
                    </td>
                    <td>
                      <div className="uw-cell-name">{app.applicant_name || '—'}</div>
                      <div className="uw-cell-sub">{[app.age || '—', app.gender || '—'].join(' / ')}</div>
                    </td>
                    <td>
                      <span className={`uw-health uw-health-${(app.health_status || '').toLowerCase()}`}>
                        {app.health_status || 'Awaiting analysis'}
                      </span>
                    </td>
                    <td>{formatPlan(app.plan)}</td>
                    <td>
                      <span className={`uw-status uw-status-${app.status || 'under_review'}`}>
                        {formatStatus(app.status)}
                      </span>
                    </td>
                    <td>{app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '—'}</td>
                    <td className="uw-cell-actions">
                      <button className="uw-action-btn uw-action-view" onClick={() => setSelectedApp(app)}>
                        Review file
                      </button>
                      <button className="uw-action-btn uw-action-ai" onClick={() => onSwitchToChat && onSwitchToChat(app.applicant_id)}>
                        Ask AI
                      </button>
                      {app.status !== 'approved' && (
                        <button className="uw-action-btn uw-action-approve" onClick={() => handleUpdateStatus(app.applicant_id, 'approved')}>
                          Approve
                        </button>
                      )}
                      {app.status !== 'rejected' && (
                        <button className="uw-action-btn uw-action-reject" onClick={() => handleUpdateStatus(app.applicant_id, 'rejected')}>
                          Reject
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </>)}

      {activeTab === 'claims' && (
        <ClaimsQueue
          claims={claims}
          filter={claimFilter}
          setFilter={setClaimFilter}
          onSelectClaim={setSelectedClaim}
          selectedClaim={selectedClaim}
          onDecision={handleClaimDecision}
          onBack={() => setSelectedClaim(null)}
          fetchClaims={fetchClaims}
        />
      )}

      {activeTab === 'preauth' && (
        <PreAuthQueue
          preauths={preauths}
          filter={preauthFilter}
          setFilter={setPreauthFilter}
          selectedPreauth={selectedPreauth}
          onSelectPreauth={setSelectedPreauth}
          onBack={() => setSelectedPreauth(null)}
          fetchPreauths={fetchPreauths}
          showToast={showToast}
        />
      )}

      {decisionModal && (
        <DecisionModal
          applicantId={decisionModal.applicantId}
          action={decisionModal.action}
          onConfirm={handleDecisionConfirm}
          onCancel={() => setDecisionModal(null)}
        />
      )}
    </div>
  );
}

const DETAIL_TABS = [
  { key: 'profile', label: 'Profile' },
  { key: 'risk', label: 'Risk Analysis' },
  { key: 'medical', label: 'Medical' },
  { key: 'decision', label: 'Decision' },
  { key: 'summary', label: 'AI Summary' },
];

function AnalysisTab({ status, content, label }) {
  if (status === 'pending') {
    return (
      <div className="uw-analysis-pending">
        <span className="uw-analysis-spinner" />
        <span>AI analysis in progress… This takes about 60 seconds after submission.</span>
      </div>
    );
  }
  if (!content) {
    return (
      <div className="uw-analysis-pending">
        <span>No {label} available. Analysis may not have run yet.</span>
      </div>
    );
  }
  return (
    <div className="uw-analysis-card">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

function ApplicationDetail({ app, onBack, onUpdateStatus, onAskAI }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    async function fetchDetail() {
      try {
        const resp = await fetch(`${API_URL}/application/${app.applicant_id}`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.found) {
            setDetail(data.application);
          }
        }
      } catch (err) {
        console.error('Failed to fetch detail:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchDetail();
  }, [app.applicant_id]);

  const record = detail || app;
  const currentStatus = app.status || record.status || 'under_review';
  const memberCount = record.members_count || record.members?.length || 1;
  const analysisStatus = record.analysis_status || null;
  const aiAnalysis = record.ai_analysis || null;

  return (
    <div className="uw-detail">
      <section className="uw-detail-hero">
        <button className="uw-back-btn" onClick={onBack}>Back to dashboard</button>

        <div className="uw-detail-hero-grid">
          <div className="uw-detail-hero-copy">
            <span className="eyebrow">Application review</span>
            <h1 className="uw-detail-title">{app.applicant_id}</h1>
            <p className="uw-detail-subtitle">
              {record.applicant_name || record.name || 'Applicant profile'} with {memberCount} insured member{memberCount === 1 ? '' : 's'}.
            </p>
            <div className="uw-detail-meta">
              <span className={`uw-status uw-status-${currentStatus}`}>
                {formatStatus(currentStatus)}
              </span>
              {analysisStatus && (
                <span className={`uw-analysis-badge uw-analysis-badge-${analysisStatus}`}>
                  {analysisStatus === 'pending' ? '⟳ AI analysis pending' : analysisStatus === 'complete' ? '✓ AI analyzed' : '⚠ Analysis error'}
                </span>
              )}
              <span className="uw-meta-chip">Submitted {record.submitted_at ? new Date(record.submitted_at).toLocaleDateString() : '—'}</span>
              <span className="uw-meta-chip">{formatPlan(record.plans || record.plan)}</span>
            </div>
          </div>

          <div className="uw-detail-sidecard">
            <span className="uw-sidecard-label">AI recommendation</span>
            <strong>{record.ai_recommendation || record.health_status || 'Awaiting analysis'}</strong>
            {record.risk_score != null && (
              <p>Risk score: <strong>{record.risk_score}/100</strong></p>
            )}
            <p>Use the AI workspace for a deeper analysis or to ask follow-up questions.</p>
          </div>
        </div>
      </section>

      {/* Tab bar */}
      <div className="uw-detail-tabs">
        {DETAIL_TABS.map(tab => (
          <button
            key={tab.key}
            className={`uw-detail-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="uw-loading surface-card">Loading application details…</div>
      ) : (
        <>
          {/* Profile tab — existing layout */}
          {activeTab === 'profile' && (
            <div className="uw-detail-layout">
              <div className="uw-detail-main">
                <section className="uw-detail-section surface-card">
                  <h3>Personal information</h3>
                  <div className="uw-detail-grid">
                    <Field label="Full name" value={record.name || record.applicant_name || '—'} />
                    <Field label="Email" value={record.email || '—'} />
                    <Field label="Mobile" value={record.mobile || '—'} />
                    <Field label="Phone" value={record.phone || '—'} />
                    <Field label="Age" value={record.age || '—'} />
                    <Field label="Gender" value={record.gender || '—'} />
                    <Field label="Date of birth" value={record.date_of_birth || '—'} />
                    <Field label="Place of birth" value={record.place_of_birth || '—'} />
                    <Field label="Nationality" value={record.nationality || '—'} />
                    <Field label="Marital status" value={record.marital_status || '—'} />
                    <Field label="Occupation" value={record.occupation || '—'} />
                    <Field label="National ID" value={record.national_id || '—'} />
                    <Field label="Financial number" value={record.financial_number || '—'} />
                    <Field label="Smoker" value={record.smoker === true ? 'Yes' : record.smoker === false ? 'No' : '—'} />

                    <Field label="Address" value={record.address || '—'} />
                  </div>
                </section>

                <section className="uw-detail-section surface-card">
                  <h3>Coverage and risk profile</h3>
                  <div className="uw-detail-grid">
                    <Field label="Selected plans" value={formatPlan(record.plans || record.plan)} />
                    <Field label="Total insured members" value={memberCount} />
                    <Field label="Dependents" value={record.dependents ?? '—'} />

                    <Field label="Previous claims" value={record.previous_claims ?? '—'} />
                    <Field
                      label="Health status"
                      value={(
                        <span className={`uw-field-value uw-health uw-health-${(record.health_status || '').toLowerCase()}`}>
                          {record.health_status || 'Awaiting analysis'}
                        </span>
                      )}
                    />
                  </div>
                </section>

                {record.members && record.members.length > 0 && (
                  <section className="uw-detail-section surface-card">
                    <h3>Insured members <span className="uw-section-count">{record.members.length}</span></h3>
                    <div className="uw-members-grid">
                      {record.members.map((m, i) => (
                        <div key={i} className="uw-member-card">
                          <div className="uw-member-header">
                            <span className="uw-member-rel">{m.relationship || 'Member'}</span>
                            <span className="uw-member-name">
                              {[m.first_name, m.middle_name, m.last_name].filter(Boolean).join(' ') || '—'}
                            </span>
                          </div>
                          <div className="uw-detail-grid">
                            <Field label="Gender" value={m.gender === 'M' ? 'Male' : m.gender === 'F' ? 'Female' : m.gender || '—'} />
                            <Field
                              label="Date of birth"
                              value={m.dob_day && m.dob_month && m.dob_year
                                ? `${String(m.dob_day).padStart(2, '0')}/${String(m.dob_month).padStart(2, '0')}/${m.dob_year}`
                                : '—'}
                            />
                            <Field label="Height" value={m.height ? `${m.height} cm` : '—'} />
                            <Field label="Weight" value={m.weight ? `${m.weight} kg` : '—'} />
                            <Field label="NSSF" value={m.nssf || '—'} />
                            <Field label="Occupation" value={m.occupation || '—'} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {record.medical_history_flags && record.medical_history_flags.some(f => f.answer) ? (
                  <section className="uw-detail-section surface-card">
                    <h3>
                      Declared medical conditions
                      <span className="uw-section-count">{record.medical_history_flags.filter(f => f.answer).length} positive</span>
                    </h3>
                    <div className="uw-med-history">
                      {record.medical_history_flags.filter(f => f.answer).map((flag, i) => (
                        <div key={i} className="uw-med-item">
                          <div className="uw-med-condition">{flag.condition}</div>
                          {flag.details && (
                            <div className="uw-detail-grid uw-med-details">
                              {flag.details.member_name && <Field label="Member" value={flag.details.member_name} />}
                              {flag.details.disease && <Field label="Disease / Diagnosis" value={flag.details.disease} />}
                              {flag.details.treatment && <Field label="Treatment" value={flag.details.treatment} />}
                              {flag.details.date && <Field label="Date" value={flag.details.date} />}
                              {flag.details.physician_hospital && <Field label="Physician / Hospital" value={flag.details.physician_hospital} />}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <details className="uw-med-negatives">
                      <summary>
                        {record.medical_history_flags.filter(f => !f.answer).length} conditions declared negative (no issues)
                      </summary>
                      <ul className="uw-neg-list">
                        {record.medical_history_flags.filter(f => !f.answer).map((flag, i) => (
                          <li key={i}>{flag.condition}</li>
                        ))}
                      </ul>
                    </details>
                  </section>
                ) : record.conditions_declared && record.conditions_declared.length > 0 ? (
                  <section className="uw-detail-section surface-card">
                    <h3>Declared medical conditions</h3>
                    <div className="uw-conditions-list">
                      {record.conditions_declared.map((cond, index) => (
                        <span key={index} className="uw-condition-tag">{cond.replace(/_/g, ' ')}</span>
                      ))}
                    </div>
                  </section>
                ) : null}

                {record.family_history && (
                  <section className="uw-detail-section surface-card">
                    <h3>Family history</h3>
                    <div className="uw-detail-grid">
                      <Field label="Heart disease" value={record.family_history.heart_disease ? 'Yes' : 'No'} />
                      <Field label="Diabetes" value={record.family_history.diabetes ? 'Yes' : 'No'} />
                      <Field label="Cancer" value={record.family_history.cancer ? 'Yes' : 'No'} />
                    </div>
                  </section>
                )}
              </div>

              <aside className="uw-decision-rail surface-card">
                <div className="uw-decision-rail-head">
                  <span className="uw-sidecard-label">Action rail</span>
                  <h3>Update underwriting status</h3>
                  <p>Record the next operational state for this file without leaving the review context.</p>
                </div>
                <div className="uw-actions-bar">
                  <button className="uw-decision-btn uw-decision-approve" onClick={() => onUpdateStatus(app.applicant_id, 'approved')}>Approve</button>
                  <button className="uw-decision-btn uw-decision-reject" onClick={() => onUpdateStatus(app.applicant_id, 'rejected')}>Reject</button>
                  <button className="uw-decision-btn uw-decision-pending" onClick={() => onUpdateStatus(app.applicant_id, 'pending_info')}>Request info</button>
                  <button className="uw-decision-btn uw-decision-escalate" onClick={() => onUpdateStatus(app.applicant_id, 'escalated')}>Escalate</button>
                  <button className="uw-decision-btn uw-decision-ai" onClick={() => onAskAI(app.applicant_id)}>Ask AI assistant</button>
                </div>
              </aside>
            </div>
          )}

          {/* AI Analysis tabs */}
          {activeTab === 'risk' && (
            <div className="uw-analysis-layout">
              <div className="uw-analysis-main">
                <AnalysisTab
                  status={analysisStatus}
                  content={aiAnalysis?.risk_assessment?.data?.risk_assessment}
                  label="Risk Analysis"
                />
              </div>
              <ActionRail app={app} onUpdateStatus={onUpdateStatus} onAskAI={onAskAI} />
            </div>
          )}

          {activeTab === 'medical' && (
            <div className="uw-analysis-layout">
              <div className="uw-analysis-main">
                <AnalysisTab
                  status={analysisStatus}
                  content={aiAnalysis?.medical_analysis?.data?.medical_analysis}
                  label="Medical Analysis"
                />
              </div>
              <ActionRail app={app} onUpdateStatus={onUpdateStatus} onAskAI={onAskAI} />
            </div>
          )}

          {activeTab === 'decision' && (
            <div className="uw-analysis-layout">
              <div className="uw-analysis-main">
                <AnalysisTab
                  status={analysisStatus}
                  content={aiAnalysis?.underwriting_decision?.data?.underwriting_decision}
                  label="Underwriting Decision"
                />
              </div>
              <ActionRail app={app} onUpdateStatus={onUpdateStatus} onAskAI={onAskAI} />
            </div>
          )}

          {activeTab === 'summary' && (
            <div className="uw-analysis-layout">
              <div className="uw-analysis-main">
                <AnalysisTab
                  status={analysisStatus}
                  content={aiAnalysis?.summary?.summary_text}
                  label="AI Summary"
                />
              </div>
              <ActionRail app={app} onUpdateStatus={onUpdateStatus} onAskAI={onAskAI} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ActionRail({ app, onUpdateStatus, onAskAI }) {
  return (
    <aside className="uw-decision-rail surface-card">
      <div className="uw-decision-rail-head">
        <span className="uw-sidecard-label">Action rail</span>
        <h3>Update underwriting status</h3>
        <p>Record the next operational state for this file.</p>
      </div>
      <div className="uw-actions-bar">
        <button className="uw-decision-btn uw-decision-approve" onClick={() => onUpdateStatus(app.applicant_id, 'approved')}>Approve</button>
        <button className="uw-decision-btn uw-decision-reject" onClick={() => onUpdateStatus(app.applicant_id, 'rejected')}>Reject</button>
        <button className="uw-decision-btn uw-decision-pending" onClick={() => onUpdateStatus(app.applicant_id, 'pending_info')}>Request info</button>
        <button className="uw-decision-btn uw-decision-escalate" onClick={() => onUpdateStatus(app.applicant_id, 'escalated')}>Escalate</button>
        <button className="uw-decision-btn uw-decision-ai" onClick={() => onAskAI(app.applicant_id)}>Ask AI assistant</button>
      </div>
    </aside>
  );
}

function DecisionModal({ applicantId, action, onConfirm, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [premiumData, setPremiumData] = useState(null);
  const [annualPremium, setAnnualPremium] = useState('');
  const [monthlyPremium, setMonthlyPremium] = useState('');
  const [conditions, setConditions] = useState('');
  const [decisionNotes, setDecisionNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (action === 'approved') {
      fetch(`${API_URL}/calculate-premium`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicant_id: applicantId }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(data.error);
          } else {
            setPremiumData(data);
            setAnnualPremium(String(data.annual_premium || 0));
            setMonthlyPremium(String(data.monthly_premium || 0));
          }
        })
        .catch(e => setError('Failed to calculate premium'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [applicantId, action]);

  const handleConfirm = () => {
    if (action === 'approved') {
      onConfirm(applicantId, 'approved', {
        annual_premium: parseInt(annualPremium, 10) || 0,
        monthly_premium: parseInt(monthlyPremium, 10) || 0,
        coverage_details: premiumData?.coverage_details || [],
        policy_start_date: premiumData?.policy_start_date || '',
        policy_end_date: premiumData?.policy_end_date || '',
        waiting_period_days: premiumData?.waiting_period_days || 30,
        pre_existing_waiting_months: premiumData?.pre_existing_waiting_months || 12,
        conditions,
        decision_notes: decisionNotes,
      });
    } else {
      onConfirm(applicantId, 'rejected', {
        conditions,
        decision_notes: decisionNotes,
      });
    }
  };

  return (
    <div className="uw-modal-overlay" onClick={onCancel}>
      <div className="uw-modal" onClick={e => e.stopPropagation()}>
        <div className="uw-modal-header">
          <h2>{action === 'approved' ? 'Approve Application' : 'Reject Application'}</h2>
          <button className="uw-modal-close" onClick={onCancel}>&times;</button>
        </div>

        <div className="uw-modal-body">
          {loading && <div className="uw-modal-loading">Calculating premium...</div>}
          {error && <div className="uw-modal-error">{error}</div>}

          {action === 'approved' && premiumData && (
            <>
              <div className="uw-modal-section">
                <h3>Premium Calculation</h3>
                <div className="uw-modal-premium-grid">
                  <div className="uw-modal-premium-card">
                    <label>Annual Premium (USD)</label>
                    <input type="number" value={annualPremium} onChange={e => {
                      setAnnualPremium(e.target.value);
                      setMonthlyPremium(String(Math.ceil(parseInt(e.target.value || '0', 10) / 12)));
                    }} />
                  </div>
                  <div className="uw-modal-premium-card">
                    <label>Monthly Premium (USD)</label>
                    <input type="number" value={monthlyPremium} readOnly />
                  </div>
                </div>
                {premiumData.factors_applied && (
                  <div className="uw-modal-factors">
                    <span>Age: {premiumData.factors_applied.age_factor}x</span>
                    <span>Health: {premiumData.factors_applied.health_factor}x</span>
                    <span>Risk: {premiumData.factors_applied.risk_factor}x</span>
                    {premiumData.factors_applied.smoker_factor > 1 && <span>Smoker: {premiumData.factors_applied.smoker_factor}x</span>}
                    {premiumData.factors_applied.member_factor > 1 && <span>Members: {premiumData.factors_applied.member_factor}x</span>}
                  </div>
                )}
              </div>

              <div className="uw-modal-section">
                <h3>Coverage Breakdown</h3>
                <table className="uw-modal-table">
                  <thead>
                    <tr><th>Plan</th><th>Limit</th><th>Copay</th><th>Deductible</th><th>Premium</th></tr>
                  </thead>
                  <tbody>
                    {(premiumData.coverage_details || []).map((plan, i) => (
                      <tr key={i}>
                        <td>{plan.plan_name}</td>
                        <td>${plan.coverage_limit?.toLocaleString()}</td>
                        <td>{plan.copay_percent}%</td>
                        <td>${plan.deductible?.toLocaleString()}</td>
                        <td>${plan.adjusted_premium?.toLocaleString()}/yr</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="uw-modal-section">
                <h3>Policy Dates</h3>
                <div className="uw-modal-dates">
                  <span>Start: {premiumData.policy_start_date}</span>
                  <span>End: {premiumData.policy_end_date}</span>
                  <span>Waiting: {premiumData.waiting_period_days} days</span>
                </div>
              </div>
            </>
          )}

          <div className="uw-modal-section">
            <h3>{action === 'approved' ? 'Special Conditions' : 'Rejection Reason'}</h3>
            <textarea
              className="uw-modal-textarea"
              placeholder={action === 'approved' ? 'Any special conditions, exclusions, or waiting periods...' : 'Provide the reason for rejection...'}
              value={conditions}
              onChange={e => setConditions(e.target.value)}
              rows={3}
            />
          </div>

          <div className="uw-modal-section">
            <h3>Underwriter Notes</h3>
            <textarea
              className="uw-modal-textarea"
              placeholder="Internal notes about this decision..."
              value={decisionNotes}
              onChange={e => setDecisionNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <div className="uw-modal-footer">
          <button className="uw-modal-btn uw-modal-btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className={`uw-modal-btn ${action === 'approved' ? 'uw-modal-btn-approve' : 'uw-modal-btn-reject'}`}
            onClick={handleConfirm}
            disabled={loading || (action === 'rejected' && !conditions.trim())}
          >
            {action === 'approved' ? 'Confirm Approval' : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="uw-field">
      <span className="uw-field-label">{label}</span>
      <span className="uw-field-value">{value}</span>
    </div>
  );
}

function formatPlan(plan) {
  if (!plan) return '—';
  if (Array.isArray(plan)) {
    return plan.map(entry => entry.replace(/_/g, ' ')).join(', ') || '—';
  }

  return String(plan).replace(/_/g, ' ');
}

function formatStatus(status) {
  const labels = {
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
    pending_info: 'Pending Info',
    escalated: 'Escalated',
    all: 'All statuses',
  };

  return labels[status] || 'Under Review';
}

function ClaimsQueue({ claims, filter, setFilter, onSelectClaim, selectedClaim, onDecision, onBack, fetchClaims }) {
  const [lobFilter, setLobFilter] = useState('all');

  if (selectedClaim) {
    return <ClaimDetailView claim={selectedClaim} onDecision={onDecision} onBack={onBack} />;
  }

  const lobOf = (c) => (c.line_of_business === 'auto' ? 'auto' : 'medical');
  const lobClaims = claims.filter(c => lobFilter === 'all' || lobOf(c) === lobFilter);
  const filteredClaims = lobClaims.filter(c => filter === 'all' || c.status === filter);

  return (
    <>
      <div className="uw-lob-filter" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {[['all', 'All Lines'], ['medical', '🩺 Medical'], ['auto', '🚗 Auto']].map(([key, label]) => (
          <button
            key={key}
            className={`uw-stat-card ${lobFilter === key ? 'active' : ''}`}
            style={{ flex: '0 0 auto', padding: '6px 14px' }}
            onClick={() => setLobFilter(key)}
          >
            <span className="uw-stat-label">{label}</span>
            <span className="uw-stat-count">{key === 'all' ? claims.length : claims.filter(c => lobOf(c) === key).length}</span>
          </button>
        ))}
      </div>

      <div className="uw-stats">
        {['all', 'submitted', 'processing', 'adjudication', 'adjudication_ready', 'approved', 'denied', 'fraud_review'].map(key => (
          <button
            key={key}
            className={`uw-stat-card ${filter === key ? 'active' : ''}`}
            onClick={() => setFilter(key)}
          >
            <span className="uw-stat-label">{key === 'all' ? 'All Claims' : key === 'adjudication_ready' ? 'Ready for Review' : key === 'fraud_review' ? 'Fraud' : key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}</span>
            <span className="uw-stat-count">{key === 'all' ? lobClaims.length : lobClaims.filter(c => c.status === key).length}</span>
          </button>
        ))}
      </div>

      <section className="uw-table-wrap surface-card">
        <div className="uw-table-head">
          <div>
            <div className="uw-table-title">Claims review queue</div>
            <p>{filteredClaims.length} claim{filteredClaims.length === 1 ? '' : 's'} match the current filter.</p>
          </div>
          <button className="uw-refresh-btn" onClick={fetchClaims}>Refresh claims</button>
        </div>

        {filteredClaims.length === 0 ? (
          <div className="uw-empty">No claims match your current filter.</div>
        ) : (
          <div className="uw-table-scroll">
            <table className="uw-table">
              <thead>
                <tr>
                  <th>Claim ID</th>
                  <th>Applicant</th>
                  <th>Line</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Pipeline</th>
                  <th>AI Recommendation</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.map(claim => (
                  <tr key={claim.claim_id}>
                    <td><div className="uw-cell-id">{claim.claim_id}</div></td>
                    <td><div className="uw-cell-name">{claim.applicant_name || '—'}</div></td>
                    <td>{claim.line_of_business === 'auto' ? '🚗 Auto' : '🩺 Medical'}</td>
                    <td>{claim.claim_type || '—'}</td>
                    <td>${Number(claim.claim_amount || claim.amount || 0).toLocaleString()}</td>
                    <td>
                      <ClaimMiniPipeline claim={claim} />
                    </td>
                    <td>
                      {claim.ai_recommendation ? (
                        <span className={`uw-status uw-status-${claim.ai_recommendation === 'Approve' ? 'approved' : claim.ai_recommendation === 'Deny' ? 'rejected' : 'under_review'}`}>
                          {claim.ai_recommendation}
                        </span>
                      ) : (
                        <span className="uw-status uw-status-pending">{claim.analysis_status === 'pending' ? '⟳ Pending' : '—'}</span>
                      )}
                    </td>
                    <td>
                      <span className={`uw-status uw-status-${claim.status || 'submitted'}`}>
                        {(claim.status || 'submitted').charAt(0).toUpperCase() + (claim.status || 'submitted').slice(1)}
                      </span>
                    </td>
                    <td>{claim.submitted_at ? new Date(claim.submitted_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <button className="uw-action-btn uw-action-view" onClick={() => onSelectClaim(claim)}>Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

const CLAIM_DETAIL_TABS = [
  { key: 'details', label: 'Claim Details' },
  { key: 'fnol', label: 'FNOL Pipeline' },
  { key: 'adjudication', label: 'Adjudication' },
  { key: 'fraud', label: 'Fraud Analysis' },
  { key: 'coverage', label: 'Coverage Assessment' },
  { key: 'document', label: 'Document Analysis' },
];

function ClaimDetailView({ claim, onDecision, onBack }) {
  const [approvedAmount, setApprovedAmount] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [activeClaimTab, setActiveClaimTab] = useState('details');
  const [fullClaim, setFullClaim] = useState(claim);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const analysisStatusRef = useRef(claim.analysis_status || 'pending');
  const fnolStatusRef = useRef(claim.fnol_status || 'not_started');

  useEffect(() => {
    async function fetchClaimDetail() {
      setLoadingDetail(true);
      try {
        const resp = await fetch(`${API_URL}/claim/${claim.claim_id}`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.claim) {
            setFullClaim(data.claim);
            analysisStatusRef.current = data.claim.analysis_status || 'pending';
            fnolStatusRef.current = data.claim.fnol_status || 'not_started';
          }
        }
      } catch (err) {
        console.error('Failed to fetch claim detail:', err);
      } finally {
        setLoadingDetail(false);
      }
    }
    fetchClaimDetail();
    const interval = setInterval(() => {
      const analysisComplete = analysisStatusRef.current === 'complete' || analysisStatusRef.current === 'error';
      const fnolComplete = fnolStatusRef.current === 'routed' || fnolStatusRef.current === 'error';
      if (!analysisComplete && !fnolComplete) {
        fetchClaimDetail();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [claim.claim_id]);

  const analysisStatus = fullClaim.analysis_status || 'pending';
  const claimAmount = fullClaim.claim_amount || fullClaim.amount || 0;

  return (
    <div className="uw-detail">
      <section className="uw-detail-hero">
        <button className="uw-back-btn" onClick={onBack}>Back to claims</button>

        <div className="uw-detail-hero-grid">
          <div className="uw-detail-hero-copy">
            <span className="eyebrow">Claim review</span>
            <h1 className="uw-detail-title">{fullClaim.claim_id}</h1>
            <p className="uw-detail-subtitle">{fullClaim.applicant_name || 'Unknown applicant'} — {fullClaim.claim_type || 'General'} claim</p>
            <div className="uw-detail-meta">
              <span className={`uw-status uw-status-${fullClaim.status || 'submitted'}`}>
                {(fullClaim.status || 'submitted').charAt(0).toUpperCase() + (fullClaim.status || 'submitted').slice(1)}
              </span>
              {analysisStatus && (
                <span className={`uw-analysis-badge uw-analysis-badge-${analysisStatus}`}>
                  {analysisStatus === 'pending' ? '⟳ AI analysis pending' : analysisStatus === 'complete' ? '✓ AI analyzed' : '⚠ Analysis error'}
                </span>
              )}
              <span className="uw-meta-chip">Amount: ${Number(claimAmount).toLocaleString()}</span>
              <span className="uw-meta-chip">Submitted {fullClaim.submitted_at ? new Date(fullClaim.submitted_at).toLocaleDateString() : '—'}</span>
            </div>
          </div>

          <div className="uw-detail-sidecard">
            <span className="uw-sidecard-label">AI recommendation</span>
            <strong>{fullClaim.ai_recommendation || 'Awaiting analysis'}</strong>
            {fullClaim.estimated_approved_amount != null && (
              <p>Estimated payout: <strong>${Number(fullClaim.estimated_approved_amount).toLocaleString()}</strong></p>
            )}
            <p>Review the fraud and coverage analysis tabs for full AI assessment.</p>
          </div>
        </div>
      </section>

      {/* Tab bar */}
      <div className="uw-detail-tabs">
        {CLAIM_DETAIL_TABS.map(tab => (
          <button
            key={tab.key}
            className={`uw-detail-tab-btn ${activeClaimTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveClaimTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loadingDetail ? (
        <div className="uw-loading surface-card">Loading claim details…</div>
      ) : (
        <>
          {/* Details tab */}
          {activeClaimTab === 'details' && (
            <div className="uw-detail-layout">
              <div className="uw-detail-main">
                <section className="uw-detail-section surface-card">
                  <h3>Claim information</h3>
                  <div className="uw-detail-grid">
                    <Field label="Claim ID" value={fullClaim.claim_id} />
                    <Field label="Applicant" value={fullClaim.applicant_name || '—'} />
                    <Field label="Applicant ID" value={fullClaim.applicant_id || '—'} />
                    <Field label="Claim Type" value={fullClaim.claim_type || '—'} />
                    <Field label="Plan Type" value={fullClaim.plan_type || '—'} />
                    <Field label="Amount" value={`$${Number(claimAmount).toLocaleString()}`} />
                    <Field label="Incident Date" value={fullClaim.incident_date || '—'} />
                    <Field label="Provider" value={fullClaim.provider_name || '—'} />
                    <Field label="Status" value={fullClaim.status || 'submitted'} />
                    <Field label="Submitted" value={fullClaim.submitted_at ? new Date(fullClaim.submitted_at).toLocaleString() : '—'} />
                  </div>
                </section>

                {fullClaim.line_of_business === 'auto' && (
                  <section className="uw-detail-section surface-card">
                    <h3>Vehicle &amp; accident</h3>
                    <div className="uw-detail-grid">
                      <Field label="Coverage Type" value={fullClaim.coverage_type || '—'} />
                      <Field label="Vehicle" value={`${(fullClaim.vehicle || {}).year || ''} ${(fullClaim.vehicle || {}).make || ''} ${(fullClaim.vehicle || {}).model || ''}`.trim() || '—'} />
                      <Field label="VIN" value={(fullClaim.vehicle || {}).vin || '—'} />
                      <Field label="Plate" value={(fullClaim.vehicle || {}).plate || '—'} />
                      <Field label="Mileage" value={(fullClaim.vehicle || {}).mileage || '—'} />
                      <Field label="Location" value={(fullClaim.accident || {}).location || '—'} />
                      <Field label="Police Report #" value={(fullClaim.accident || {}).police_report_no || '—'} />
                      <Field label="Fault Party" value={(fullClaim.accident || {}).fault_party || '—'} />
                      <Field label="Drivable" value={(fullClaim.accident || {}).drivable || '—'} />
                    </div>
                  </section>
                )}

                {fullClaim.description && (
                  <section className="uw-detail-section surface-card">
                    <h3>Description</h3>
                    <p style={{ lineHeight: '1.6', color: 'var(--text-secondary)' }}>{fullClaim.description}</p>
                  </section>
                )}

                {fullClaim.decided_at && (
                  <section className="uw-detail-section surface-card">
                    <h3>Decision record</h3>
                    <div className="uw-detail-grid">
                      <Field label="Decision" value={fullClaim.status} />
                      <Field label="Decided at" value={new Date(fullClaim.decided_at).toLocaleString()} />
                      {fullClaim.approved_amount != null && <Field label="Approved amount" value={`$${Number(fullClaim.approved_amount).toLocaleString()}`} />}
                      {fullClaim.denial_reason && <Field label="Denial reason" value={fullClaim.denial_reason} />}
                      {fullClaim.decision_notes && <Field label="Notes" value={fullClaim.decision_notes} />}
                    </div>
                  </section>
                )}
              </div>

              <aside className="uw-decision-rail surface-card">
                <div className="uw-decision-rail-head">
                  <span className="uw-sidecard-label">Claim decision</span>
                  <h3>Process this claim</h3>
                </div>
                <div className="uw-actions-bar" style={{ flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Approved amount ($)</label>
                    <input type="number" value={approvedAmount} onChange={e => setApprovedAmount(e.target.value)} placeholder={fullClaim.estimated_approved_amount ? `AI suggests $${Number(fullClaim.estimated_approved_amount).toLocaleString()}` : 'Enter amount'} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border)' }} />
                  </div>
                  <button className="uw-decision-btn uw-decision-approve" onClick={() => onDecision(fullClaim.claim_id, 'approved', parseFloat(approvedAmount) || 0, '')} disabled={!approvedAmount}>Approve claim</button>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Denial reason</label>
                    <textarea value={denyReason} onChange={e => setDenyReason(e.target.value)} placeholder="Reason for denial" rows={3} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border)' }} />
                  </div>
                  <button className="uw-decision-btn uw-decision-reject" onClick={() => onDecision(fullClaim.claim_id, 'denied', 0, denyReason)} disabled={!denyReason.trim()}>Deny claim</button>
                </div>
              </aside>
            </div>
          )}

          {/* Fraud Analysis tab */}
          {activeClaimTab === 'fraud' && (
            <div className="uw-analysis-layout">
              <div className="uw-analysis-main">
                <AnalysisTab
                  status={analysisStatus}
                  content={fullClaim.fraud_analysis_text}
                  label="Fraud Analysis"
                />
              </div>
              <aside className="uw-decision-rail surface-card">
                <div className="uw-decision-rail-head">
                  <span className="uw-sidecard-label">Fraud detection</span>
                  <h3>AI fraud assessment</h3>
                  <p>Uses enterprise_fraud_detection MCP tool to analyze patterns, timing, and red flags.</p>
                </div>
              </aside>
            </div>
          )}

          {/* FNOL Pipeline tab */}
          {activeClaimTab === 'fnol' && (
            <FnolPipelineTab claim={fullClaim} />
          )}

          {/* Adjudication Pipeline tab */}
          {activeClaimTab === 'adjudication' && (
            <AdjudicationPipelineTab claim={fullClaim} />
          )}

          {/* Coverage Assessment tab */}
          {activeClaimTab === 'coverage' && (
            <div className="uw-analysis-layout">
              <div className="uw-analysis-main">
                <AnalysisTab
                  status={analysisStatus}
                  content={fullClaim.coverage_analysis_text}
                  label="Coverage Assessment"
                />
              </div>
              <aside className="uw-decision-rail surface-card">
                <div className="uw-decision-rail-head">
                  <span className="uw-sidecard-label">Coverage validity</span>
                  <h3>AI coverage assessment</h3>
                  <p>Uses enterprise_claim_decision MCP tool to evaluate policy coverage, dates, and payout estimation.</p>
                </div>
                {fullClaim.estimated_approved_amount != null && (
                  <div style={{ marginTop: '12px', padding: '12px', background: 'var(--surface-alt)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>AI estimated payout</span>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--accent)' }}>${Number(fullClaim.estimated_approved_amount).toLocaleString()}</div>
                  </div>
                )}
              </aside>
            </div>
          )}

          {/* Document Analysis tab */}
          {activeClaimTab === 'document' && (
            <div className="uw-analysis-layout">
              <div className="uw-analysis-main">
                {fullClaim.supporting_document_parsed && Object.keys(fullClaim.supporting_document_parsed).length > 0 ? (
                  <section className="uw-detail-section surface-card">
                    <h3>Parsed supporting document</h3>
                    <div className="uw-detail-grid">
                      <Field label="Document type" value={fullClaim.supporting_document_parsed.document_type || '—'} />
                      <Field label="Provider" value={fullClaim.supporting_document_parsed.provider_name || '—'} />
                      <Field label="Service date" value={fullClaim.supporting_document_parsed.service_date || '—'} />
                      <Field label="Total amount" value={fullClaim.supporting_document_parsed.total_amount != null ? `$${Number(fullClaim.supporting_document_parsed.total_amount).toLocaleString()}` : '—'} />
                      <Field label="Currency" value={fullClaim.supporting_document_parsed.currency || '—'} />
                      <Field label="Diagnosis / Description" value={fullClaim.supporting_document_parsed.diagnosis_or_description || '—'} />
                      {fullClaim.supporting_document_parsed.notes && <Field label="Notes" value={fullClaim.supporting_document_parsed.notes} />}
                    </div>
                    {fullClaim.supporting_document_parsed.items && fullClaim.supporting_document_parsed.items.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <h4 style={{ marginBottom: '8px' }}>Line items</h4>
                        <table className="uw-table" style={{ fontSize: '13px' }}>
                          <thead><tr><th>Description</th><th>Amount</th></tr></thead>
                          <tbody>
                            {fullClaim.supporting_document_parsed.items.map((item, i) => (
                              <tr key={i}><td>{item.description}</td><td>${Number(item.amount || 0).toLocaleString()}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                ) : (
                  <div className="uw-analysis-pending">
                    <span>{fullClaim.document_parsed ? 'Document was parsed but details are not available in this view.' : 'No supporting document was uploaded with this claim.'}</span>
                  </div>
                )}
              </div>
              <aside className="uw-decision-rail surface-card">
                <div className="uw-decision-rail-head">
                  <span className="uw-sidecard-label">Document intelligence</span>
                  <h3>AI document parsing</h3>
                  <p>Supporting documents are automatically parsed using Amazon Bedrock to extract key data fields.</p>
                </div>
              </aside>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Claim Mini Pipeline — shows FNOL + Adjudication progress dots
   ═══════════════════════════════════════════════════════════════ */
function ClaimMiniPipeline({ claim }) {
  const fnolPipeline = claim.fnol_pipeline_status || {};
  const adjPipeline = claim.adj_pipeline_status || {};
  const fnolKeys = claim.line_of_business === 'auto'
    ? ['intake', 'valuation', 'damage', 'liability', 'subrogation', 'fraud_signals', 'routing']
    : ['intake', 'ocr', 'construction', 'completeness', 'preauth_link', 'fraud_signals', 'routing'];
  const adjKeys = ['case_assembly', 'policy', 'clinical', 'pricing', 'cob', 'recommendation', 'review_package', 'settlement'];

  const hasFnol = Object.keys(fnolPipeline).length > 0;
  const hasAdj = Object.keys(adjPipeline).length > 0;

  if (!hasFnol && !hasAdj) {
    return <span style={{ fontSize: '0.75rem', color: '#64748b' }}>—</span>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {hasFnol && (
        <div className="provider-mini-pipeline" title="FNOL Pipeline">
          {fnolKeys.map(key => {
            const st = fnolPipeline[key] || 'pending';
            return <span key={key} className={`mini-step ${st === 'complete' || st === 'skipped' ? 'step-done' : st === 'in_progress' ? 'step-active' : 'step-pending'}`} />;
          })}
        </div>
      )}
      {hasAdj && (
        <div className="provider-mini-pipeline" title="Adjudication Pipeline">
          {adjKeys.map(key => {
            const st = adjPipeline[key] || 'pending';
            return <span key={key} className={`mini-step ${st === 'complete' ? 'step-done' : st === 'in_progress' ? 'step-active' : 'step-pending'}`} />;
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FNOL Pipeline Visualization Tab
   ═══════════════════════════════════════════════════════════════ */
const FNOL_STEPS = [
  { key: 'intake', label: 'Intake', icon: '📥', field: 'fnol_intake_result' },
  { key: 'ocr', label: 'OCR', icon: '📄', field: 'fnol_ocr_result' },
  { key: 'construction', label: 'Construction', icon: '🏗️', field: 'fnol_construction_result' },
  { key: 'completeness', label: 'Completeness', icon: '✓', field: 'fnol_completeness_result' },
  { key: 'preauth_link', label: 'Pre-Auth Link', icon: '🔗', field: 'fnol_preauth_linkage_result' },
  { key: 'fraud_signals', label: 'Fraud Signals', icon: '🚨', field: 'fnol_fraud_signals_result' },
  { key: 'routing', label: 'Routing', icon: '🔀', field: 'fnol_routing_result' },
];

const AUTO_FNOL_STEPS = [
  { key: 'intake', label: 'Intake', icon: '📥', field: 'fnol_intake_result' },
  { key: 'valuation', label: 'Vehicle Valuation', icon: '🚗', field: 'fnol_valuation_result' },
  { key: 'damage', label: 'Damage Assessment', icon: '💥', field: 'fnol_damage_result' },
  { key: 'liability', label: 'Liability', icon: '⚖️', field: 'fnol_liability_result' },
  { key: 'subrogation', label: 'Subrogation', icon: '🔁', field: 'fnol_subrogation_result' },
  { key: 'fraud_signals', label: 'Fraud Signals', icon: '🚨', field: 'fnol_fraud_signals_result' },
  { key: 'routing', label: 'Routing', icon: '🔀', field: 'fnol_routing_result' },
];

function PhotoGallery({ urls }) {
  if (!urls || urls.length === 0) return null;
  return (
    <section className="uw-detail-section surface-card" style={{ marginBottom: '16px' }}>
      <h3>📸 Accident Photos ({urls.length})</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
        {urls.map((url, i) => (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt={`Accident photo ${i + 1}`}
              style={{ width: '160px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border-color, #ddd)' }} />
          </a>
        ))}
      </div>
    </section>
  );
}

function FnolPipelineTab({ claim }) {
  const pipeline = claim.fnol_pipeline_status || {};
  const fnolStatus = claim.fnol_status || 'not_started';
  const isAuto = claim.line_of_business === 'auto';
  const steps = isAuto ? AUTO_FNOL_STEPS : FNOL_STEPS;

  return (
    <div className="uw-analysis-layout">
      <div className="uw-analysis-main">
        {isAuto && <PhotoGallery urls={claim.photo_urls} />}
        <section className="uw-detail-section surface-card">
          <h3>{isAuto ? 'Auto FNOL Pipeline — First Notice of Loss' : 'FNOL Pipeline — First Notice of Loss'}</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            {isAuto
              ? '7-step AI auto-claim pipeline: intake, vehicle valuation, photo damage assessment, liability, subrogation, fraud, and routing.'
              : '7-step AI intake pipeline that normalizes, validates, and routes claims.'}
            {' '}Status: <strong>{fnolStatus === 'not_started' ? 'Not Started' : fnolStatus}</strong>
            {claim.fnol_route_destination && <> → Routed to: <strong>{claim.fnol_route_destination.replace(/_/g, ' ')}</strong></>}
          </p>

          {/* Pipeline visualization */}
          <div className="provider-pipeline-full" style={{ marginBottom: '24px' }}>
            {steps.map((step) => {
              const stepStatus = pipeline[step.key] || 'pending';
              const cls = stepStatus === 'complete' ? 'pipeline-done' : stepStatus === 'in_progress' ? 'pipeline-active' : stepStatus === 'skipped' ? 'pipeline-done' : '';
              return (
                <div key={step.key} className={`pipeline-step-full ${cls}`}>
                  <div className="pipeline-dot" />
                  <span className="pipeline-step-label">
                    <span className="pipeline-step-icon">{step.icon}</span>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Step results */}
          {steps.map(step => {
            const result = claim[step.field];
            if (!result) return null;
            return (
              <details key={step.key} className="provider-step-detail">
                <summary>{step.icon} {step.label} — {(pipeline[step.key] || 'pending').replace(/_/g, ' ')}</summary>
                <pre>{result}</pre>
              </details>
            );
          })}
        </section>
      </div>
      <aside className="uw-decision-rail surface-card">
        <div className="uw-decision-rail-head">
          <span className="uw-sidecard-label">FNOL Agent</span>
          <h3>Intake Pipeline</h3>
          <p>Runs 7 MCP tools to normalize, validate, and route claims automatically.</p>
        </div>
        {claim.fnol_route_destination && (
          <div style={{ marginTop: '12px', padding: '12px', background: 'var(--surface-alt)', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Route destination</span>
            <div style={{ fontSize: '16px', fontWeight: '700', marginTop: '4px', textTransform: 'capitalize' }}>
              {claim.fnol_route_destination.replace(/_/g, ' ')}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Adjudication Pipeline Visualization Tab
   ═══════════════════════════════════════════════════════════════ */
const ADJ_STEPS = [
  { key: 'case_assembly', label: 'Case Assembly', icon: '📋', field: 'adj_case_assembly_result' },
  { key: 'policy', label: 'Policy', icon: '📜', field: 'adj_policy_result' },
  { key: 'clinical', label: 'Clinical', icon: '🏥', field: 'adj_clinical_result' },
  { key: 'pricing', label: 'Pricing', icon: '💰', field: 'adj_pricing_result' },
  { key: 'cob', label: 'COB', icon: '🔄', field: 'adj_cob_result' },
  { key: 'recommendation', label: 'Recommendation', icon: '⚖️', field: 'adj_recommendation_result' },
  { key: 'review_package', label: 'Review Package', icon: '📦', field: 'adj_review_package_result' },
  { key: 'settlement', label: 'Settlement', icon: '💳', field: 'adj_settlement_result' },
];

function AdjudicationPipelineTab({ claim }) {
  const pipeline = claim.adj_pipeline_status || {};
  const adjStatus = claim.adjudication_status || 'not_started';

  return (
    <div className="uw-analysis-layout">
      <div className="uw-analysis-main">
        <section className="uw-detail-section surface-card">
          <h3>Adjudication-Assist Pipeline</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            8-step AI pipeline that prepares a recommendation for human adjudicators.
            Status: <strong>{adjStatus === 'not_started' ? 'Not Started' : adjStatus.replace(/_/g, ' ')}</strong>
            {claim.adj_suggested_action && <> → Suggested: <strong>{claim.adj_suggested_action.replace(/_/g, ' ')}</strong> ({claim.adj_confidence || 'medium'} confidence)</>}
          </p>

          {/* Pipeline visualization */}
          <div className="provider-pipeline-full" style={{ marginBottom: '24px' }}>
            {ADJ_STEPS.map((step) => {
              const stepStatus = pipeline[step.key] || 'pending';
              const cls = stepStatus === 'complete' ? 'pipeline-done' : stepStatus === 'in_progress' ? 'pipeline-active' : '';
              return (
                <div key={step.key} className={`pipeline-step-full ${cls}`}>
                  <div className="pipeline-dot" />
                  <span className="pipeline-step-label">
                    <span className="pipeline-step-icon">{step.icon}</span>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Step results */}
          {ADJ_STEPS.map(step => {
            const result = claim[step.field];
            if (!result) return null;
            return (
              <details key={step.key} className="provider-step-detail">
                <summary>{step.icon} {step.label} — {(pipeline[step.key] || 'pending').replace(/_/g, ' ')}</summary>
                <pre>{result}</pre>
              </details>
            );
          })}

          {adjStatus === 'not_started' && (
            <div className="provider-empty">
              Adjudication pipeline has not started. Claims are routed to adjudication after the FNOL pipeline determines they need human review.
            </div>
          )}
        </section>
      </div>
      <aside className="uw-decision-rail surface-card">
        <div className="uw-decision-rail-head">
          <span className="uw-sidecard-label">Adjudication Agent</span>
          <h3>Decision Support</h3>
          <p>Runs 8 MCP tools to assemble the case, interpret policy, review clinical appropriateness, and generate a recommendation.</p>
        </div>
        {claim.adj_suggested_action && (
          <div style={{ marginTop: '12px', padding: '12px', background: 'var(--surface-alt)', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>AI Recommendation</span>
            <div style={{ fontSize: '16px', fontWeight: '700', marginTop: '4px', textTransform: 'capitalize' }}>
              {claim.adj_suggested_action.replace(/_/g, ' ')}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              Confidence: {claim.adj_confidence || 'medium'}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PreAuthQueue — Ops view for pre-authorization requests
   ═══════════════════════════════════════════════════════════════ */
const PREAUTH_STEPS = [
  { key: 'eligibility', label: 'Eligibility', icon: '✓', statusKey: 'checking_eligibility', resultField: 'ai_eligibility_result' },
  { key: 'benefits', label: 'Benefits', icon: '📋', statusKey: 'checking_benefits', resultField: 'ai_benefit_result' },
  { key: 'medical_necessity', label: 'Medical Necessity', icon: '🏥', statusKey: 'checking_necessity', resultField: 'ai_medical_necessity_result' },
  { key: 'coding', label: 'Coding', icon: '🔢', statusKey: 'checking_coding', resultField: 'ai_coding_result' },
  { key: 'tariff', label: 'Tariff', icon: '💰', statusKey: 'checking_tariff', resultField: 'ai_tariff_result' },
  { key: 'decision', label: 'Decision', icon: '⚖️', statusKey: 'deciding', resultField: 'ai_decision_result' },
];

const TERMINAL_STATUSES = ['approved', 'denied', 'routed_to_human', 'info_requested', 'error'];

function derivePreauthPipeline(pa) {
  const currentStatus = pa.status || '';
  const isComplete = TERMINAL_STATUSES.includes(currentStatus);
  const currentStepIdx = PREAUTH_STEPS.findIndex(s => s.statusKey === currentStatus);

  const pipeline_status = {};
  const step_results = {};

  PREAUTH_STEPS.forEach((step, i) => {
    const hasResult = pa[step.resultField] && pa[step.resultField] !== '{}';
    if (isComplete || hasResult) {
      pipeline_status[step.key] = 'complete';
    } else if (i === currentStepIdx) {
      pipeline_status[step.key] = 'in_progress';
    } else {
      pipeline_status[step.key] = 'pending';
    }
    if (hasResult) {
      step_results[step.key] = pa[step.resultField];
    }
  });

  return { ...pa, pipeline_status, step_results, patient_name: pa.member_name };
}

// ═══════════════════════════════════════════════════════════════════
// PRE-AUTH DETAIL VIEW WITH LIVE POLLING
// ═══════════════════════════════════════════════════════════════════
function PreAuthDetailView({ preauth: initialPreauth, onBack, handleDecision, API_URL }) {
  const [pa, setPa] = useState(derivePreauthPipeline(initialPreauth));
  const statusRef = useRef(initialPreauth.status);
  const isTerminal = TERMINAL_STATUSES.includes(pa.status);

  useEffect(() => {
    if (isTerminal) return;
    const interval = setInterval(async () => {
      try {
        const resp = await fetch(`${API_URL}/preauth/${pa.preauth_id}`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.preauth_id) {
            const derived = derivePreauthPipeline(data);
            setPa(derived);
            statusRef.current = data.status;
            if (TERMINAL_STATUSES.includes(data.status)) {
              clearInterval(interval);
            }
          }
        }
      } catch (err) { /* ignore */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [pa.preauth_id, isTerminal, API_URL]);

  const pipeline = pa.pipeline_status || {};

  return (
    <section className="uw-preauth-detail" style={{ padding: '0 24px' }}>
      <button className="provider-back-btn" onClick={onBack}>← Back to queue</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'monospace', fontSize: '1.4rem' }}>{pa.preauth_id}</h2>
        <span className="provider-status-badge" style={{ background: statusColor(pa.status) }}>{pa.status?.replace(/_/g, ' ')}</span>
        {!isTerminal && <span style={{ fontSize: '0.8rem', color: '#3b82f6', animation: 'pulse 1.5s infinite' }}>● Processing...</span>}
      </div>

      {/* Pipeline visualization */}
      <div className="provider-pipeline-full" style={{ marginBottom: '24px' }}>
        {PREAUTH_STEPS.map((step) => {
          const stepStatus = pipeline[step.key] || 'pending';
          const cls = stepStatus === 'complete' ? 'pipeline-done' : stepStatus === 'in_progress' ? 'pipeline-active' : '';
          return (
            <div key={step.key} className={`pipeline-step-full ${cls}`}>
              <div className="pipeline-dot" />
              <span className="pipeline-step-label">
                <span className="pipeline-step-icon">{step.icon}</span>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div className="provider-detail-section">
          <h3>Request Info</h3>
          <div className="provider-detail-fields">
            <div><strong>Provider:</strong> {pa.provider_name || pa.provider_id}</div>
            <div><strong>Patient:</strong> {pa.member_name} ({pa.applicant_id})</div>
            <div><strong>Service:</strong> {pa.requested_service?.cpt_code} — {pa.requested_service?.description}</div>
            <div><strong>Diagnosis:</strong> {pa.diagnosis?.icd10_code} — {pa.diagnosis?.description || pa.diagnosis?.clinical_notes?.slice(0, 60)}</div>
            <div><strong>Amount:</strong> ${Number(pa.requested_amount || 0).toLocaleString()}</div>
            <div><strong>Urgency:</strong> {pa.urgency}</div>
            <div><strong>Submitted:</strong> {pa.submitted_at?.replace('T', ' ').slice(0, 19)}</div>
          </div>
        </div>
        <div className="provider-detail-section">
          <h3>Clinical Notes</h3>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>{pa.diagnosis?.clinical_notes || 'None provided'}</pre>
        </div>
      </div>

      {/* AI Decision summary (shown when complete) */}
      {isTerminal && pa.ai_recommendation && (
        <div className="provider-detail-section" style={{ marginBottom: '24px' }}>
          <h3>AI Decision</h3>
          <div className="provider-detail-fields">
            <div><strong>Recommendation:</strong> <span className="provider-status-badge" style={{ background: statusColor(pa.ai_recommendation) }}>{pa.ai_recommendation}</span></div>
            {Number(pa.estimated_approved_amount) > 0 && <div><strong>Approved Amount:</strong> ${Number(pa.estimated_approved_amount).toLocaleString()}</div>}
            {pa.decided_at && <div><strong>Decided:</strong> {pa.decided_at?.replace('T', ' ').slice(0, 19)}</div>}
          </div>
        </div>
      )}

      {/* Step results */}
      {pa.step_results && Object.keys(pa.step_results).length > 0 && (
        <div className="provider-detail-section" style={{ marginBottom: '24px' }}>
          <h3>Pipeline Step Results</h3>
          {PREAUTH_STEPS.map(step => {
            const result = pa.step_results?.[step.key];
            if (!result) return null;
            return (
              <details key={step.key} className="provider-step-detail">
                <summary>{step.icon} {step.label}</summary>
                <pre>{typeof result === 'string' ? result : JSON.stringify(result, null, 2)}</pre>
              </details>
            );
          })}
        </div>
      )}

      {/* Decision rail for routed_to_human */}
      {pa.status === 'routed_to_human' && (
        <div style={{ display: 'flex', gap: '12px', padding: '20px', background: '#fffbeb', borderRadius: '12px', border: '1px solid #fde68a' }}>
          <span style={{ fontSize: '1.2rem' }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong>Manual Review Required</strong>
            <p style={{ margin: '4px 0 12px', fontSize: '0.88rem', color: '#78350f' }}>This request needs human decision.</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="provider-btn-primary" onClick={() => handleDecision(pa.preauth_id, 'approved')}>Approve</button>
              <button className="provider-btn-primary" style={{ background: '#dc2626' }} onClick={() => handleDecision(pa.preauth_id, 'denied')}>Deny</button>
              <button className="provider-btn-primary" style={{ background: '#6b7280' }} onClick={() => handleDecision(pa.preauth_id, 'request_info')}>Request Info</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function PreAuthQueue({ preauths: rawPreauths, filter, setFilter, selectedPreauth: rawSelectedPreauth, onSelectPreauth, onBack, fetchPreauths, showToast }) {
  const preauths = rawPreauths.map(derivePreauthPipeline);
  const selectedPreauth = rawSelectedPreauth ? derivePreauthPipeline(rawSelectedPreauth) : null;
  const API_URL_LOCAL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const statusCounts = preauths.reduce((acc, p) => {
    acc.all = (acc.all || 0) + 1;
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, { all: 0 });

  const filtered = filter === 'all' ? preauths : preauths.filter(p => p.status === filter);

  const handleDecision = async (preauthId, decision) => {
    try {
      const resp = await fetch(`${API_URL_LOCAL}/preauth-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preauth_id: preauthId, decision, decided_by: 'ops_user' }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      showToast(`${preauthId} → ${decision}`, decision === 'denied' ? 'error' : 'success');
      fetchPreauths();
      onBack();
    } catch (err) {
      showToast('Decision failed: ' + err.message, 'error');
    }
  };

  if (selectedPreauth) {
    return <PreAuthDetailView preauth={selectedPreauth} onBack={onBack} handleDecision={handleDecision} API_URL={API_URL_LOCAL} />;
  }

  return (
    <section style={{ padding: '0 24px' }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {['all', 'processing', 'approved', 'denied', 'routed_to_human', 'pending_info'].map(s => (
          <button
            key={s}
            className={`seg-btn ${filter === s ? 'seg-btn-active' : ''}`}
            onClick={() => setFilter(s)}
          >
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')} ({statusCounts[s] || 0})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="provider-empty">No pre-authorization requests found.</div>
      ) : (
        <div className="provider-requests-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Patient</th>
                <th>Procedure</th>
                <th>Provider</th>
                <th>Status</th>
                <th>Pipeline</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(pa => (
                <tr key={pa.preauth_id} className="provider-row-clickable" onClick={() => onSelectPreauth(pa)}>
                  <td className="provider-cell-id">{pa.preauth_id}</td>
                  <td>{pa.patient_name}</td>
                  <td>{pa.procedure_code}</td>
                  <td>{pa.provider_id}</td>
                  <td><span className="provider-status-badge" style={{ background: statusColor(pa.status) }}>{pa.status}</span></td>
                  <td>
                    <div className="provider-mini-pipeline">
                      {PREAUTH_STEPS.map(step => {
                        const st = (pa.pipeline_status || {})[step.key] || 'pending';
                        return <span key={step.key} className={`mini-step ${st === 'complete' ? 'step-done' : st === 'in_progress' ? 'step-active' : 'step-pending'}`} />;
                      })}
                    </div>
                  </td>
                  <td>{new Date(pa.submitted_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function statusColor(status) {
  const map = { approved: '#059669', denied: '#dc2626', processing: '#3b82f6', routed_to_human: '#d97706', pending_info: '#6b7280' };
  return map[status] || '#64748b';
}

export default UnderwriterPortal;
